import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageDwell, pathKind } from '../services/analytics';

// Tracks how long a visitor spends on each route and beacons it on route change
// and on tab-hide/close. Mount <PageTracker /> once inside the Router.
export function usePageTracking() {
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  const enteredRef = useRef(Date.now());

  // Flush the previous route's dwell whenever the path changes.
  useEffect(() => {
    const prevPath = pathRef.current;
    const now = Date.now();
    if (prevPath && prevPath !== location.pathname) {
      trackPageDwell(prevPath, now - enteredRef.current, pathKind(prevPath));
    }
    pathRef.current = location.pathname;
    enteredRef.current = now;
  }, [location.pathname]);

  // Flush the current route's dwell when the tab is hidden or unloaded.
  useEffect(() => {
    const flush = () => {
      const now = Date.now();
      trackPageDwell(pathRef.current, now - enteredRef.current, pathKind(pathRef.current));
      enteredRef.current = now; // don't double-count if the tab is re-shown
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, []);
}

// Render-null component so the hook can live inside the Router without touching
// the App component's own structure.
export function PageTracker() {
  usePageTracking();
  return null;
}
